import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";

const SESSION_KEY = "visitor_session_id";
const CHANNEL_NAME = "realtime_visitors";

interface VisitorPresence {
  session_id: string;
  page_path: string;
  page_title: string;
  joined_at: string;
  last_activity: string;
  device_type: string;
  browser: string;
  country?: string;
  city?: string;
  ip_hash?: string;
  hostname?: string;
  isp?: string;
}

// Get or create session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "Tablet";
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return "Mobile";
  return "Desktop";
};

// Detect browser
const getBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("SamsungBrowser")) return "Samsung";
  if (ua.includes("Opera") || ua.includes("OPR")) return "Opera";
  if (ua.includes("Edge")) return "Edge";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Safari")) return "Safari";
  return "Other";
};

export const useRealtimePresence = () => {
  const location = useLocation();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const geoDataRef = useRef<{
    country?: string;
    city?: string;
    ip_hash?: string;
    hostname?: string;
    isp?: string;
  }>({});
  const isTrackingRef = useRef(false);

  // Fetch geo data once
  const fetchGeoData = useCallback(async () => {
    if (geoDataRef.current.country) return; // Already fetched
    
    try {
      const response = await supabase.functions.invoke("track-visitor");
      if (response.data) {
        geoDataRef.current = {
          country: response.data.country,
          city: response.data.city,
          ip_hash: response.data.ip_hash,
          hostname: response.data.hostname,
          isp: response.data.isp,
        };
      }
    } catch (error) {
      console.error("Error fetching geo data for presence:", error);
    }
  }, []);

  // Track presence
  const trackPresence = useCallback(async () => {
    if (!channelRef.current || isTrackingRef.current) return;
    
    const sessionId = getSessionId();
    const presenceData: VisitorPresence = {
      session_id: sessionId,
      page_path: location.pathname,
      page_title: document.title,
      joined_at: new Date().toISOString(),
      last_activity: new Date().toISOString(),
      device_type: getDeviceType(),
      browser: getBrowser(),
      ...geoDataRef.current,
    };

    try {
      await channelRef.current.track(presenceData);
      isTrackingRef.current = true;
    } catch (error) {
      console.error("Error tracking presence:", error);
    }
  }, [location.pathname]);

  // Initialize channel and subscribe
  useEffect(() => {
    const channel = supabase.channel(CHANNEL_NAME, {
      config: {
        presence: {
          key: getSessionId(),
        },
      },
    });

    channelRef.current = channel;

    channel.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        await fetchGeoData();
        await trackPresence();
      }
    });

    return () => {
      if (channelRef.current) {
        channelRef.current.untrack();
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
        isTrackingRef.current = false;
      }
    };
  }, [fetchGeoData, trackPresence]);

  // Update presence on page change
  useEffect(() => {
    if (channelRef.current && isTrackingRef.current) {
      const sessionId = getSessionId();
      const presenceData: VisitorPresence = {
        session_id: sessionId,
        page_path: location.pathname,
        page_title: document.title,
        joined_at: new Date().toISOString(),
        last_activity: new Date().toISOString(),
        device_type: getDeviceType(),
        browser: getBrowser(),
        ...geoDataRef.current,
      };

      channelRef.current.track(presenceData).catch(console.error);
    }
  }, [location.pathname]);

  // Update last activity periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (channelRef.current && isTrackingRef.current) {
        const sessionId = getSessionId();
        const presenceData: VisitorPresence = {
          session_id: sessionId,
          page_path: location.pathname,
          page_title: document.title,
          joined_at: new Date().toISOString(),
          last_activity: new Date().toISOString(),
          device_type: getDeviceType(),
          browser: getBrowser(),
          ...geoDataRef.current,
        };

        channelRef.current.track(presenceData).catch(console.error);
      }
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [location.pathname]);

  return null;
};
